/**
 * Effective enabled-state derivation for a feature flag.
 *
 * The lifecycle dot (active/draft/archived) does NOT tell an operator whether a
 * flag is actually ON. We surface the *baseline* behavior — what a user who
 * matches no targeting rule and isn't singled out by rollout gets — which is
 * driven by default_value + rollout_percentage.
 *
 * Targeting rules are narrow exceptions (in practice almost every flag carries a
 * platform guard like `platform not_in [ios,android] → false`), so treating
 * "has rules" as the headline state made the whole list read "partial" and hid
 * the real on/off. Rules are therefore reported separately via `hasRules` and
 * rendered as a secondary modifier, never as the primary state.
 */

import type { FlagResponse } from '@/types/featureFlags';

export type FlagDisplayState = 'on' | 'off' | 'partial' | 'draft' | 'archived';

export interface FlagStateInfo {
  /** Baseline state from default_value + rollout. `partial` = gradual rollout. */
  state: FlagDisplayState;
  /** Rollout percentage (only meaningful for `partial`). */
  rollout: number;
  /** Whether targeting rules exist — a modifier, not part of `state`. */
  hasRules: boolean;
}

export function getFlagDisplayState(flag: FlagResponse): FlagStateInfo {
  const rollout = flag.rollout_percentage ?? 0;
  const hasRules = (flag.targeting_rules?.length ?? 0) > 0;
  const out = (state: FlagDisplayState): FlagStateInfo => ({ state, rollout, hasRules });

  if (flag.lifecycle_state === 'archived') return out('archived');
  if (flag.lifecycle_state === 'draft') return out('draft');

  // Baseline only. Evaluator (evaluator.py): rollout ENABLES on top of default
  // (never turns a user off); default=true is on for everyone regardless of rollout.
  if (flag.default_value === true) return out('on');
  if (rollout >= 100) return out('on');
  if (rollout <= 0) return out('off');
  return out('partial');
}

/** Tailwind classes for the colored status pill, keyed by state. */
export const FLAG_STATE_PILL: Record<FlagDisplayState, string> = {
  on: 'bg-green-100 text-green-700',
  off: 'bg-gray-200 text-gray-600',
  partial: 'bg-amber-100 text-amber-700',
  draft: 'bg-slate-100 text-slate-600',
  archived: 'bg-gray-100 text-gray-400',
};

/** Solid dot color for the compact list indicator, keyed by state. */
export const FLAG_STATE_DOT: Record<FlagDisplayState, string> = {
  on: 'bg-green-500',
  off: 'bg-gray-300',
  partial: 'bg-amber-500',
  draft: 'bg-slate-400',
  archived: 'bg-gray-300',
};
